import type { CmsBundle, ProductList } from 'modules/basket/types';
import { appConfig } from 'modules/config/appConfig';
import type {
  HBOStatusResponse,
  ShippingOrders,
  StreamingServicesStatusResponse,
  TransactionsResponse,
} from './api.types';
import { apiService } from './bootstrap';
import { utcToDisplayDate } from './dateUtils';
import type {
  AgreementId,
  CommercialId,
  CustomerResponse,
  DevicesResponse,
  HardwareProductsResponse,
  OfferId,
  SoftwareProductsApiResponse,
  UpdateCustomerRequest,
} from './services/api.types';
import type { AwaitedReturnType } from './typeUtils';

export const getUser = () =>
  apiService.requestJson<CustomerResponse>({
    url: `/customer`,
    method: 'GET',
  });

/**
 * `POST /customer`
 */
export const saveUser = (data: UpdateCustomerRequest) =>
  apiService.request({
    url: '/customer',
    method: 'POST',
    payload: data,
  });

/**
 * OTT status:
 * - `Green`: less than 30 days since last OTT login
 * - `Orange`: more than 30 days since last OTT login
 * - `Red`: no OTT login on file
 *
 * Details at: https://git.rikstv.no/rikstv/volum/services/RiksTV.Services.ConsumerProductsEventLogProvider/-/blob/master/src/RiksTV.Services.ConsumerProductsEventLogProvider.Web/Controllers/WssEventsReaderController.cs?ref_type=heads#L79
 */
export type OTTStatus = 'Green' | 'Orange' | 'Red';
export const getOTTStatus = () =>
  apiService
    .request({
      url: '/customer/OTTStatus',
      method: 'GET',
    })
    .then((r) => r.text() as Promise<OTTStatus>);

export const restoreTVAccess = () =>
  apiService.request({
    url: '/provisioning/restoreTVAccess',
    method: 'POST',
  });

export const getHardwareProducts = () =>
  apiService
    .requestJson<HardwareProductsResponse>({
      url: '/hardwareproducts',
      method: 'GET',
    })
    .then((json) =>
      json.map((product) => ({
        ...product,
        contractEndDate: utcToDisplayDate(product.contractEndDate),
        /** Format: DD.MM.YYYY */
        contractStartDate: utcToDisplayDate(product.contractStartDate),
      })),
    );

export type ApiSoftwareProduct = ApiSoftwareProducts[number];
export type ApiSoftwareProducts = AwaitedReturnType<typeof getSoftwareProducts>;
export const getSoftwareProducts = () =>
  apiService
    .requestJson<SoftwareProductsApiResponse>({
      url: '/softwareproducts',
      method: 'GET',
    })
    .then((json) =>
      json.map((product) => ({
        ...product,
        contractEndDate: utcToDisplayDate(product.contractEndDate),
        contractStartDate: utcToDisplayDate(product.contractStartDate),
      })),
    );

export type CardWithSubscription = {
  agreementDetailId: AgreementId;
  linkedSubscriptions: {
    agreementDetailId: AgreementId;
    commercialProductId: CommercialId;
    name: string;
  }[];
};
export const linkSubscriptionsWithCards = (subscriptionsLinksUpdate: CardWithSubscription[]) =>
  apiService.request({
    url: `/devicelinks/updatelinksaction`,
    method: 'POST',
    payload: {
      SubscriptionCards: subscriptionsLinksUpdate,
    },
  });

/**
 * `POST /customer/consents`
 */
export const updateConsents = (customerConsents: CustomerResponse['customerConsents']) =>
  apiService.request({
    url: `/customer/consents`,
    method: 'POST',
    payload: customerConsents,
  });

export const addHardwareProduct = (boxSerialNumber: string, cardSerialNumber: string, isBundledDevice: boolean) =>
  apiService.request({
    url: `/hardwareproducts`,
    method: 'POST',
    payload: {
      boxSerialNumber,
      cardSerialNumber,
      isBundledDevice,
    },
  });

// PURCHASE PRODUCTS
//--------------------

// Generic purchase endpoint

export type OrderProductBundlePayload = {
  hardware: { customerOfferId: OfferId; hardwareProducts: ProductList };
  software: { customerOfferId: OfferId | null; softwareProducts: ProductList; channelIdList: number[] };
};
export const orderProductBundle = (bundle: OrderProductBundlePayload, provider: number | string, token: string) =>
  apiService.request({
    url: `/products/order`,
    method: 'POST',
    payload: {
      ...bundle,
      provider: provider,
      accesstoken: token,
    },
  });

// Campaign purchase
// export const orderCampaignProducts = (campaignId, hardware, software, contractPeriod, channelIdList = []) =>
//     apiService.request({
//         url: `/products/campaignorder`,
//         method: 'POST',
//         payload: {
//             "CampaignId": campaignId,
//             "Hardware": {
//                 "ContractPeriod": contractPeriod,
//                 "CustomerOfferId": 166,
//                 "HardwareProducts": hardware
//             },
//             "Software": {
//                 "ContractPeriod": contractPeriod,
//                 "CustomerOfferId": null,
//                 "ChannelIdList": channelIdList,
//                 "SoftwareProducts": software
//             }
//         }
//     });

export type CampaignOrderPayload = OrderProductBundlePayload & {
  campaignId: string;
};
export const orderCampaignProducts = (campaignData: CampaignOrderPayload) =>
  apiService.request({
    url: '/products/campaignorder',
    method: 'POST',
    payload: campaignData,
  });

// UPGRADE / DOWNGRADE
//---------------------

export type UpgradeDowngradePayload = {
  agreementDetailId: AgreementId;
  newCommercialProductId: CommercialId;
  oldCommercialProductId: CommercialId;
  channelIdList: number[];
  executionDate?: string;
  contractEndDate?: string;
  contractPeriod: number | undefined;
  offerId: OfferId | undefined;
  reason?: number;
};

export const postRegrade = (payload: UpgradeDowngradePayload) =>
  apiService.request({
    url: '/products/downgrade', // TODO: change API to /products/regrade
    method: 'POST',
    payload,
  });

// Cancel software
export const cancelSoftwareProducts = (ids: AgreementId[]) =>
  apiService.request({
    url: '/softwareproducts/cancel',
    method: 'POST',
    payload: {
      SoftwareAgreementDetailIds: ids,
    },
  });

// 404 means the device is already gone, which is what was asked for: removed in another tab, or by an
// earlier request the client never heard back from, after the list was loaded
export const deleteHardwareProduct = (agreementDetailId: AgreementId) =>
  apiService.request({
    url: `/hardwareproducts/${agreementDetailId}`,
    method: 'DELETE',
    okStatusCodes: [404],
  });

export const getDevices = (serialNumber: string) =>
  apiService.requestJson<DevicesResponse>({
    url: `/devices?serialNumber=${serialNumber}`,
    method: 'GET',
  });

// Add product with offer
export const addProductOffer = (payload: UpgradeDowngradePayload) =>
  apiService.request({
    url: `/products/${payload.agreementDetailId}/offers`,
    method: 'POST',
    payload,
  });

export const validatechurnprofilingoffers = (agreementDetailsId: AgreementId, offerIds: OfferId[]): Promise<boolean> =>
  apiService.requestJson({
    url: `/products/${agreementDetailsId}/validatechurnprofilingoffers`,
    method: 'POST',
    payload: { offerIds },
  });

// FINANCE
//----------
export const getFinancialBalance = () =>
  apiService.requestJson<{ balance: number; inDebtCollection: boolean; dueDate?: string }>({
    url: '/finance/balance',
    method: 'GET',
  });

// Get the Bambora payment link from the PaymentService
export const getPaymentSession = (amount: number, orderId: number, invoiceId: string | undefined) =>
  apiService
    .requestJson<{ url: string; sessionId: string }>({
      url: '/payment',
      method: 'POST',
      payload: {
        order: {
          amount,
          id: `${orderId}`,
          currency: 'NOK',
          invoiceId: invoiceId, //NOTE: this is an InvoiceID(in ICC) not an invoice number!!!
        },
        url: {
          accept: `${window.location.origin}/betalingen-fullfort`,
          cancel: `${window.location.origin}/betalingen-feilet`,
        },
      },
    })
    .then((res) => {
      console.log('getPaymentSession', res);
      return res;
    });

export const fetchTaxInvoices = () =>
  apiService
    .requestJson<Array<{ id: number; invoiceNumber: number; amount: number }>>({
      url: `/finance/taxinvoices`,
      method: 'GET',
    })
    .then((json) =>
      json.map((invoice) => ({
        ...invoice,
        isPdfAvailable: true,
        isTaxInvoice: true,
      })),
    );

type Invoice = {
  dueDate?: string;
  amount: number;
  id: string;
  nextUrl?: NextInvoiceUrl;
  status: string;
};
export type NextInvoiceUrl = `finance/invoices?page=${number}`;

export const fetchInvoicePage = (nextPage: NextInvoiceUrl = 'finance/invoices?page=1') =>
  apiService
    .requestJson<{ nextUrl?: NextInvoiceUrl; resources: Invoice[] }>({
      url: `/${nextPage}`,
      method: 'GET',
    })
    .then(({ resources, nextUrl }) => ({
      nextUrl,
      resources: resources.map((invoice) => ({
        ...invoice,
        dueDate: utcToDisplayDate(invoice.dueDate),
        isPdfAvailable: ['Ok', 'Dunning1', 'Dunning2'].includes(invoice.status),
      })),
    }));

// TVoD
export const fetchTransactionsPage = (page: number, itemsPerPage: number) =>
  apiService.requestJson<TransactionsResponse>({
    url: `${appConfig.tvodApiBaseUrl}/tvod/transactions?page=${page}&pageSize=${itemsPerPage}&sortOrder=desc`,
    method: 'GET',
  });
export const fetchTvodVoucherInfo = () =>
  apiService.requestJson<{ freeRentalVoucherCount: number }>({
    url: `${appConfig.tvodApiBaseUrl}/tvod/voucherinfo`,
    method: 'GET',
  });

// export const fetchOffersForAgreementDetails = (agreementDetailsId: AgreementId) =>
//   apiService
//     .requestJson<any[]>({
//       url: `/offers/${agreementDetailsId}`,
//       method: 'GET',
//     })
//     .then((json) =>
//       json.map((offer) => ({
//         ...offer,
//         endDate: utcToDisplayDate(offer.endDate),
//         startDate: utcToDisplayDate(offer.startDate),
//         agreementDetailsId,
//       })),
//     );

// Generic product list
export const fetchProductOffers = () =>
  apiService.requestJson<CmsBundle[]>({
    url: '/products/offers',
    method: 'GET',
  });

export const downloadPdf = (invoiceNumber: number) =>
  apiService.requestPdfFile({
    url: `/finance/invoicepdf?number=${invoiceNumber}`,
    filename: `Faktura ${invoiceNumber}.pdf`,
  });

export const fetchGdprFilesStatus = () =>
  apiService.requestJson<{ displayName: string; key: string }[]>({
    url: '/customer/gdprfiles',
    method: 'GET',
  });

export const fetchPreSignedGdprFile = (fileKey: string) =>
  apiService.requestJson({
    url: `/customer/gdprfiles/presigned?fileKey=${fileKey}`,
    method: 'GET',
  });

export const fetchCustomerKeywords = () =>
  apiService
    .requestJson<Array<{ id: number; keywordId: number; keywordTypeId: number; name: string; attribute: string }>>({
      url: `/customer/keywords`,
      method: 'GET',
    })
    .then((res) =>
      res.map((k) => ({ name: k.name, attribute: k.attribute, typeId: k.keywordTypeId, templateId: k.keywordId })),
    );

// CAMPAIGNS
export type CampaignData = {
  name: string;
  id: string;
  isValid: boolean;
  totalNonRecurring: number;
  totalRecurring: number;
  originalNonRecurring: number;
  originalRecurring: number;
  products: Array<{ commercialProductId: CommercialId; offerId?: OfferId; skiporiginalprice: boolean }>;
};
export const fetchCampaignData = (campaignDocumentId: string) =>
  apiService.requestJson<CampaignData>({
    url: `/campaign/${campaignDocumentId}`,
    method: 'GET',
  });

// FINANCIAL OPTIONS
//-------------------

export type ChargePeriod = 'Monthly' | 'Quarterly' | 'HalfYearly' | 'Yearly';
export type DueDate = 'TheFifteenth' | 'TheTwentyThird';
export type PaymentMethod = 'NormalFaktura' | 'AvtaleGiro' | 'EFaktura' | 'EFakturaAvtaleGiro';
export type FinanceOptionsResponse = {
  chargePeriod: ChargePeriod | null;
  dueDate: DueDate | null;
  paymentMethod: PaymentMethod | null;
  kid: string;
};
export const getFinancialOptions = () =>
  apiService.requestJson<FinanceOptionsResponse>({
    url: '/finance/options',
    method: 'GET',
  });

export const setChargePeriod = (period: ChargePeriod) =>
  apiService.request({
    url: `/financialoptions/setchargeperiod`,
    method: 'POST',
    payload: {
      chargePeriod: period,
    },
  });

export const setDueDate = (dueDate: DueDate) =>
  apiService.request({
    url: `/financialoptions/setduedate`,
    method: 'POST',
    payload: {
      dueDate,
    },
  });

export const setPaymentMethod = (method: PaymentMethod) =>
  apiService.request({
    url: `/financialoptions/setpaymentmethod`,
    method: 'POST',
    payload: {
      method,
    },
  });

// ORDERS
//--------

export const getShippingOrders = () =>
  apiService.requestJson<ShippingOrders | undefined>({
    url: `/shippingorders`,
    method: 'GET',
    /** 404 is expected when there are no shipping orders */
    okStatusCodes: [404],
  });

// export const getInstallationOrders = () =>
//   apiService.requestJson({
//     url: `/installationorders`,
//     method: 'GET',
//   });

// A LA CARTE CHANNELS
export type AlaCarteChannel = {
  channelId: number;
  name: string;
  type: 'TvChannel';
  isAlaCarte: boolean;
  distributionMethod: 'dtt' | 'ott';
  description?: string;
  iconUrl?: string;
  weight: number;
};
export const getAlacarteChannels = (commercialProductId: CommercialId) =>
  apiService.requestJson<{
    commercialProductId: CommercialId;
    channelList: AlaCarteChannel[];
  }>({
    url: `/channels/alacarte/${commercialProductId}`,
    method: 'GET',
  });
export type ApiAlaCarteChannelResponse = AwaitedReturnType<typeof getAlacarteChannels>;

type CustomerChannelsApiResponse = {
  channels: Array<{ id: number; name: string; active: boolean }>;
  details: {
    commercialProductId: CommercialId;
    hasAlaCarteSubscription: boolean;
    maximumChannelsCount: number;
    remainingChannelsCount: number;
    technicalProductId: number;
  };
};
export const getCustomerChannels = (commercialProductId: CommercialId) =>
  apiService
    .requestJson<CustomerChannelsApiResponse>({
      url: `/customer/alacarte/${commercialProductId}`,
      method: 'GET',
    })
    .then((res) => {
      // These channels are now available to all users by default, but some users have these in their selection
      // TODO: Cant we just update all users in the database instead of doing this here?
      const removedChannels = [1, 2, 3, 19, 40, 42, 58];
      return {
        details: res.details,
        channels: res.channels.filter((c) => !removedChannels.includes(c.id)),
      };
    });

export const saveCustomerChannels = (commercialProductId: CommercialId, channelIds: number[]) =>
  apiService.request({
    url: `/customer/alacarte/${commercialProductId}`,
    method: 'POST',
    payload: channelIds,
  });

// FREEZE

export const getFreezeDetails = () =>
  apiService.requestJson<{ uniqueFreezeEventsActiveDays: number; uniqueFreezeEventsCount: number }>({
    url: '/customer/freezedetails',
    method: 'GET',
  });

// export const getProductFreezeDetails = (agreementDetailsId: AgreementId) =>
//   apiService
//     .requestJson<SoftwareProductsApiResponse[0] & { agreementDetailsId: AgreementId }>({
//       url: `/products/${agreementDetailsId}/freezedetails`,
//       method: 'GET',
//     })
//     .then((json) => ({ ...json, agreementDetailsId }));

export const freezeAccount = (startDate: string, endDate: string) =>
  apiService.request({
    url: '/customer/schedulefreeze',
    method: 'POST',
    payload: { startDate, endDate },
  });

export const unfreezeAccount = () =>
  apiService.request({
    url: '/customer/unfreeze',
    method: 'POST',
  });

export const freezeProducts = (
  list: Array<{
    commercialProductId: CommercialId;
    agreementDetailsId: AgreementId;
    startDate: string;
    endDate: string;
  }>,
) =>
  apiService.request({
    url: '/products/schedulefreeze',
    method: 'POST',
    payload: list,
  });

export const unfreezeProducts = (list: AgreementId[]) =>
  apiService.request({
    url: '/products/unfreeze',
    method: 'POST',
    payload: list,
  });

// PIN
export const fetchPinSettings = () =>
  apiService.requestJson<{ purchasePinActive: boolean }>({
    url: `/pin/settings`,
    method: 'GET',
  });

export const postValidatePin = (pin: string) =>
  apiService.requestJson<{ isValid: boolean }>({
    url: `/pin/validate`,
    method: 'POST',
    payload: { pin },
  });

export const postUpdatedPin = (purchasePin: string) =>
  apiService.requestJson({
    url: `/pin/settings/purchasepin`,
    method: 'POST',
    payload: { purchasePin },
  });

export const postPurchasePinEnabled = (enabled: boolean) =>
  apiService.request({
    url: `/pin/settings/purchasepinactive`,
    method: 'POST',
    payload: { purchasePinActive: enabled },
  });

// PRICES
export const getPrices = (
  commercialProductsWithOffers: Array<{
    commercialProductId: CommercialId;
    offerId?: OfferId;
  }>,
) =>
  apiService.requestJson<
    Array<{
      commercialProductId: CommercialId;
      isRecurring: boolean;
      offerId?: OfferId;
      price: number;
      originalPrice: number;
      originalInvoiceText: string;
      offerEndDate?: string;
    }>
  >({
    url: '/price',
    method: 'POST',
    payload: commercialProductsWithOffers,
  });

type ActivateSoftwarePayload = {
  softwareProductsToActivate: Array<{ agreementDetailsId: AgreementId }>;
};
export const activateProducts = (payload: ActivateSoftwarePayload) =>
  apiService.request({
    url: '/activate/software',
    method: 'POST',
    payload: payload,
  });

export const tv2ContinueProvisioning = () =>
  apiService.request({ url: '/provisioning/tv2Play/continueProvisioning', method: 'PUT' });

export const tv2Consent = () => apiService.request({ url: '/provisioning/tv2Play/consent', method: 'PUT' });

export const tv2getProvisioningUrl = (redirectUrl: string) => {
  const queryParams = new URLSearchParams({ redirectUrl }).toString();
  return apiService.requestJson({ url: `/provisioning/tv2Play/getProvisioningUrl?${queryParams}`, method: 'GET' });
};
export type Tv2PlayStatus =
  | 'Completed'
  | 'NeedsToCancelTv2PlaySubscription'
  | 'NeedsToSetPassword'
  | 'NeedsConnection'
  | 'Connected'
  | 'NotFound'
  | 'Deactivated'
  | 'Unknown';

export type Tv2PlayStatusResponse = {
  status: Tv2PlayStatus;
};
export const getTv2Status = () =>
  apiService.requestJson<Tv2PlayStatusResponse>({
    url: '/provisioning/tv2Play/getStatus',
    method: 'GET',
  });

export const getHboStatus = () =>
  apiService.requestJson<HBOStatusResponse>({
    url: '/provisioning/hbo/status',
    method: 'GET',
    okStatusCodes: [404],
  });

export const putHboMaxStatus = () =>
  apiService.requestJson<{ activationUrl?: string; isActivated?: boolean }>({
    url: '/provisioning/hbo/status',
    method: 'PUT',
  });

// Configured in API for all envs but localhost, uat will otherwise redirect to minside.uat.rikstv.no/callback
const getLocalhostCallbackParam = () =>
  location.hostname === 'localhost' ? `&callbackUrl=${location.origin}/callback` : '';

export const initCustomerVerification = (providerName: string) =>
  apiService.request({
    url: `/customerverification/init?provider=${providerName}${getLocalhostCallbackParam()}`,
    method: 'GET',
  });

export const getAuthToken = (code: string, providerName: string) =>
  apiService.requestJson<{ access_token: string; scope: string; id_token: string }>({
    url: `/customerverification/getAuthToken?code=${code}&provider=${providerName}${getLocalhostCallbackParam()}`,
    method: 'GET',
  });

export const getVerifiedUserData = (accessToken: string, providerName: string) =>
  apiService.requestJson<{
    name: string;
    givenName: string;
    familiyName: string;
    phoneNumber: string;
    email: string;
    birthdate?: string;
    address?: {
      formattedAddress: string;
      streetWithNumber: string;
      postalCode: string;
      city: string;
    };
  }>({
    url: `/customerverification/getVerifiedUserData?provider=${providerName}`,
    method: 'POST',
    payload: { accessToken },
  });

// Viaplay
export type ViaplayAccountStatus = 'NotFound' | 'NonRegistered' | 'Active';
export const getViaplayAccountStatus = () =>
  apiService.requestJson<{ customerId: number; status: ViaplayAccountStatus }>({
    url: `/provisioning/viaplay/status`,
    method: 'GET',
  });

// Combined streaming services status
export const getStreamingServicesStatus = () =>
  apiService.requestJson<StreamingServicesStatusResponse>({
    url: '/streamingservices',
    method: 'GET',
  });

// Cancellation request
export const sendCancellationRequest = (cancellationRequest: {
  productsToCancel: CommercialId[];
  reasonsForCancellation: (number | undefined)[];
  competitors: number[];
  comment: string | null;
}) =>
  apiService.request({
    url: '/registerCancellationRequest',
    method: 'POST',
    payload: cancellationRequest,
  });
